import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from '../../utils/i18n';
import { 
  Palette, 
  Sun, 
  Moon, 
  Monitor, 
  Globe, 
  RotateCcw,
  Check,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Appearance Settings Page - Phase 9.1
 * 
 * Unified appearance preferences:
 * - Accent color presets
 * - Light/Dark/System mode
 * - Language selection
 */
const AppearanceSettings = () => {
  const { 
    accentPreset, 
    setAccentPreset, 
    appearanceMode, 
    setAppearanceMode,
    language,
    setLanguage,
    resetToDefaults,
    accentPresets,
    appearanceModes,
    languageOptions,
    isDark,
  } = useTheme();
  
  const { t } = useTranslation(language);

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'light': return Sun;
      case 'dark': return Moon;
      default: return Monitor;
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="appearance-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-stone-900 dark:text-white">
          {t('appearance.title')}
        </h1>
        <p className="text-stone-500 dark:text-stone-400 mt-1">
          {t('appearance.subtitle')}
        </p>
      </div>

      {/* Accent Color */}
      <section className="bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Palette className="text-emerald-600 dark:text-emerald-400" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
              {t('appearance.accentColor')}
            </h2>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('appearance.accentColorDesc')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.values(accentPresets).map((preset) => {
            const isSelected = accentPreset === preset.id;
            return (
              <button
                key={preset.id}
                onClick={() => setAccentPreset(preset.id)}
                className={`relative p-4 rounded-xl border-2 transition-all text-left group ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600'
                }`}
                data-testid={`accent-preset-${preset.id}`}
              >
                {/* Color swatch */}
                <div 
                  className="w-8 h-8 rounded-full mb-3 shadow-md"
                  style={{ backgroundColor: preset.colors.accent }}
                />
                
                {/* Label */}
                <div className="font-medium text-sm text-stone-900 dark:text-white">
                  {t(`accent.${preset.id}`)}
                </div>
                <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                  {preset.description}
                </div>

                {/* Check mark */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Appearance Mode */}
      <section className="bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            {isDark ? (
              <Moon className="text-emerald-600 dark:text-emerald-400" size={20} />
            ) : (
              <Sun className="text-emerald-600 dark:text-emerald-400" size={20} />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
              {t('appearance.mode')}
            </h2>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('appearance.modeDesc')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {Object.values(appearanceModes).map((mode) => {
            const isSelected = appearanceMode === mode.id;
            const Icon = getModeIcon(mode.id);
            return (
              <button
                key={mode.id}
                onClick={() => setAppearanceMode(mode.id)}
                className={`flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600'
                }`}
                data-testid={`appearance-mode-${mode.id}`}
              >
                <Icon size={20} className={isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-stone-500'} />
                <span className={`font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-stone-700 dark:text-stone-300'}`}>
                  {t(`appearance.${mode.id}`)}
                </span>
                {isSelected && (
                  <Check size={16} className="text-emerald-500 ml-1" />
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Language */}
      <section className="bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Globe className="text-emerald-600 dark:text-emerald-400" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
              {t('appearance.language')}
            </h2>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('appearance.languageDesc')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {Object.values(languageOptions).map((lang) => {
            const isSelected = language === lang.id;
            return (
              <button
                key={lang.id}
                onClick={() => setLanguage(lang.id)}
                className={`flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600'
                }`}
                data-testid={`language-${lang.id}`}
              >
                <span className="text-xl">{lang.flag}</span>
                <div>
                  <div className={`font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-stone-700 dark:text-stone-300'}`}>
                    {lang.name}
                  </div>
                  <div className="text-xs text-stone-500 dark:text-stone-400">
                    {lang.nativeName}
                  </div>
                </div>
                {isSelected && (
                  <Check size={16} className="text-emerald-500 ml-2" />
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Reset to Defaults */}
      <section className="bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-slate-700 flex items-center justify-center">
              <RotateCcw className="text-stone-600 dark:text-stone-400" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">
                {t('appearance.resetDefaults')}
              </h2>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                {t('appearance.resetDesc')}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={resetToDefaults}
            className="shrink-0"
            data-testid="reset-defaults-btn"
          >
            {t('common.reset')}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AppearanceSettings;
